import { ChevronRight, Eye, Settings01, User01, Users01, XClose } from "@untitledui/icons";
import React, { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { AccessibilityPanel } from "@/features/AccessibilityPanel/AccessibilityPanel";
import type { AppDispatch } from "@/store";
import { selectCommonConfig, selectRoles, selectSelectedRole, setSelectedRole } from "@/store/roleSlice";
import type { ThemeName } from "@/store/theme";
import { selectTheme, setThemeAndPersist } from "@/store/theme";

import { getInitials } from "../utils/profile";
import { ProfileModal } from "./ProfileModal";

interface ProfileMenuProps {
  displayName: string;
  email?: string;
  isAuthenticated: boolean;
  onLogin: () => void;
  onLogout: () => void;
}

/**
 * ✅ FULL ROLE LABEL MAPPING
 */
const ROLE_LABELS: Record<string, string> = {
  PPP: "Program Point Person",
  FPP: "Finance Point Person",
  LEGAL: "Legal Point Person",
  PD: "Country Director/Program Director",
  ED: "Executive Director",
  TECHNICAL: "Technical Reviewer",
  IMPACT: "Impact Reviewer",
  POP: "Program Operations and Planning",
  PARTNER: "Partner",
  CHIEFS: "Chiefs",
};

const ProfileMenu: React.FC<ProfileMenuProps> = ({
  displayName,
  email,
  isAuthenticated,
  onLogin,
  onLogout,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useSelector(selectTheme) as ThemeName;
  const { t } = useTranslation();

  const roles = useSelector(selectRoles);
  const selectedRole = useSelector(selectSelectedRole);

  const [open, setOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);

  const [accessibilityOpen, setAccessibilityOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profile, setProfile] = useState<any>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const menuId = useId();

const config = useSelector(selectCommonConfig);

const themeOptions = [
  config?.VibrantTheme
    ? {
        key: "theme1" as ThemeName,
        label: t("themes.vibrant", { defaultValue: "Vibrant Theme" }),
      }
    : null,
  config?.LightTheme
    ? {
        key: "theme2" as ThemeName,
        label: t("themes.light", { defaultValue: "Light Theme" }),
      }
    : null,
  config?.DarkTheme
    ? {
        key: "theme3" as ThemeName,
        label: t("themes.dark", { defaultValue: "Dark Theme" }),
      }
    : null,
].filter((item): item is { key: ThemeName; label: string } => item !== null);


  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        e.target instanceof Node &&
        !containerRef.current.contains(e.target)
      ) {
        setOpen(false);
        setThemeOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  /**
   * ✅ OPEN PROFILE WITH FULL ROLE NAME
   */
  const openProfile = () => {
    setOpen(false);
    setProfile({
      displayName,
      email,
      organization: "MasterCard Foundation",
      role: ROLE_LABELS[selectedRole] || selectedRole || "User",
    });
    setProfileOpen(true);
  };

  return (
    <>
      <AccessibilityPanel open={accessibilityOpen} onClose={() => setAccessibilityOpen(false)} />

      <ProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} profile={profile} />

      <div ref={containerRef} className="relative">
        {/* ✅ Trigger */}
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 px-1 py-1 rounded-md hover:bg-[var(--sidebar-hover)]"
        >
          <span className="h-8 w-8 flex items-center justify-center rounded-full bg-[var(--color-brand)] text-white text-xs font-bold">
            {getInitials(displayName)}
          </span>

          <span className="hidden sm:inline text-sm font-medium text-[var(--text-primary)]">
            {displayName}
          </span>
        </button>

        {/* ✅ Dropdown */}
        {open && (
          <div
            id={menuId}
            className="absolute right-0 mt-2 w-[340px] rounded-xl shadow-xl z-[1100] bg-[var(--bg-surface)] border border-[var(--border-color)] transition-colors duration-500"
          >
            {/* ✅ Header */}
            <div className="px-4 py-4 border-b border-[var(--border-color)] flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-[var(--color-brand)] text-white flex items-center justify-center font-semibold">
                {getInitials(displayName)}
              </div>
              <div>
                <div className="text-sm font-medium text-[var(--text-primary)]">{displayName}</div>
                {email && <div className="text-xs text-gray-500">{email}</div>}
              </div>
            </div>

            {/* ✅ Role Section */}
            {roles.length > 1 && (
              <div className="px-4 py-3 border-b border-[var(--border-color)]">
                <div
                  className="text-xs text-gray-500 mb-2 uppercase"
                  aria-label={t("viewApplication", { defaultValue: "View Application As" })}
                >
                  {t("viewApplication", {
                    defaultValue: "View Application As",
                  })}
                </div>

                {roles.map((role) => {
                  const active = role === selectedRole;

                  return (
                    <button
                      key={role}
                      onClick={() => {
                        dispatch(setSelectedRole(role));
                        setOpen(false);
                      }}
                      className={`flex w-full items-center justify-between px-3 py-2 rounded-md text-sm ${
                        active ? "bg-[var(--select-bg)]" : "hover:bg-[var(--select-bg)]"
                      }`}
                    >
                      {/* ✅ FULL ROLE NAME HERE */}
                      <span className="text-[var(--text-primary)]">
                        {ROLE_LABELS[role] || role}
                      </span>

                      <span
                        className="h-4 w-4 rounded-full border flex items-center justify-center"
                        style={{
                          borderColor: active ? "var(--color-brand)" : "var(--border-color)",
                        }}
                      >
                        {active && (
                          <span
                            className="h-2 w-2 rounded-full"
                            style={{ background: "var(--color-brand)" }}
                          />
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* ✅ Menu Items */}
            <div className="py-2 border-b border-[var(--border-color)]">
              <button
                onClick={openProfile}
                className="flex w-full items-center gap-3 px-4 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--select-bg)]"
              >
                <Users01 className="h-4 w-4" />
                {t("viewProfile.title", { defaultValue: "View Profile" })}
              </button>

              {/* ✅ Themes */}
              <div className="relative">
                <button
                  onClick={() => setThemeOpen((v) => !v)}
                  className="flex w-full items-center gap-3 px-4 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--select-bg)]"
                >
                  <Eye className="h-4 w-4" />
                  {t("themes.title", { defaultValue: "Themes" })}
                  <ChevronRight className="ml-auto h-4 w-4 text-gray-400" />
                </button>

                {themeOpen && (
                  <div className="absolute top-0 right-full mr-2 w-[220px] rounded-xl shadow-xl bg-[var(--bg-surface)] border border-[var(--border-color)]">
                    <div className="px-4 py-3 border-b border-[var(--border-color)] text-sm font-medium text-[var(--text-primary)]">
                      {t("themes.title", { defaultValue: "Themes" })}
                    </div>

                    {themeOptions.map((opt) => {
                      const selected = theme === opt.key;

                      return (
                        <button
                          key={opt.key}
                          onClick={() => dispatch(setThemeAndPersist(opt.key))}
                          className={`flex w-full items-center justify-between px-4 py-2 text-sm ${
                            selected ? "bg-[var(--select-bg)]" : "hover:bg-[var(--select-bg)]"
                          }`}
                        >
                          <span className="text-[var(--text-primary)]">{opt.label}</span>

                          <span
                            className="h-4 w-4 rounded-full border flex items-center justify-center"
                            style={{
                              borderColor: selected ? "var(--color-brand)" : "var(--border-color)",
                            }}
                          >
                            {selected && (
                              <span
                                className="h-2 w-2 rounded-full"
                                style={{ background: "var(--color-brand)" }}
                              />
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <button
                onClick={() => {
                  setOpen(false);
                  setAccessibilityOpen(true);
                }}
                className="flex w-full items-center gap-3 px-4 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--select-bg)]"
              >
                <Settings01 className="h-4 w-4" />
                {t("accessibilityTitle", {
                  defaultValue: "Accessibility Settings",
                })}
              </button>
            </div>

            {/* ✅ Auth */}
            <div className="py-2">
              {isAuthenticated ? (
                <button
                  onClick={() => {
                    setOpen(false);
                    onLogout();
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2 text-sm text-[var(--color-brand)] hover:bg-[var(--select-bg)]"
                >
                  <XClose className="h-4 w-4" />
                  {t("signOut", { defaultValue: "Sign Out" })}
                </button>
              ) : (
                <button
                  onClick={() => {
                    setOpen(false);
                    onLogin();
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--select-bg)]"
                >
                  <User01 className="h-4 w-4" />
                  {t("signIn", { defaultValue: "Sign In" })}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ProfileMenu;
